import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { HttpService } from "src/app/core/services/http.service";
import {
  AuthenticationStep,
  LoginRequest,
} from "src/app/modules/authentication/models/login";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AuthCoordinatorService } from "src/app/modules/authentication/services/auth-coordinator.service";
import {
  AuthenticationMethodType,
  Flow,
  FlowResponse,
  FlowStep,
  FlowTypeCode,
  QrCodeResponse,
  VerifyOtpRequest,
} from "../models/flow";
import { getFlowResponse, getQrCode } from "../serializers/flow-serializer";

@Injectable({
  providedIn: "root",
})
export class FlowService {
  private authenticationStep?: AuthenticationStep;
  private activeFlowStep: FlowStep | null = null;
  private flow: Flow | null = null;
  private flowAccessToken?: string;
  private resendOtpTimer?: number;
  private otpToken?: string;

  constructor(
    private authCoordinatorService: AuthCoordinatorService,
    private accountService: AccountService,
    private httpService: HttpService,
  ) {}

  createLoginFlow(loginRequest: LoginRequest): Observable<FlowResponse | null> {
    return this.httpService.post("auth/flows/", loginRequest).pipe(
      map((response) => {
        if (response?.data) {
          return this.processFlowResponse(response.data);
        }
        return null;
      }),
    );
  }

  saveAuthenticationMethod(authenticationMethodType: AuthenticationMethodType) {
    return this.httpService.post("auth/flows/steps/auth-methods", {
      method: authenticationMethodType,
    });
  }

  getCurrentFlow() {
    return this.httpService.get("auth/flows").pipe(
      map((response) => {
        if (response?.data) {
          return this.processFlowResponse(response.data);
        }
        return null;
      }),
    );
  }

  getQrCode(): Observable<QrCodeResponse | null> {
    return this.httpService.post("auth/flows/steps/2fa/qr-codes").pipe(
      map((response) => {
        if (response?.data) {
          return getQrCode(response.data);
        }
        return null;
      }),
    );
  }

  verifyOtpForTwoFactorAuthentication(code: string) {
    return this.httpService.post("auth/flows/steps/2fa/verification", { code });
  }

  verifyOtp(params: VerifyOtpRequest) {
    return this.httpService.post("auth/flows/steps/otp/verification", params);
  }

  /**
   * Handles the response from the flow and updates the application state.
   * @param response The response received from the flow.
   * @returns The parsed FlowResponse or null if the response is falsy.
   */
  private processFlowResponse(response: any): FlowResponse | null {
    // Check if the response is truthy
    if (response) {
      // Extract FlowResponse from the response
      const flowResponse = getFlowResponse(response);

      // Check if the FlowResponse contains a valid flow
      if (flowResponse?.flow) {
        // Update application state with the new flow and active step
        this.setFlow(flowResponse.flow);
        this.setActiveFlowStep(flowResponse.flow.activeStep);
      }

      // Update application state with the flow access token
      this.setFlowAccessToken(flowResponse?.flowAccessToken ?? "");

      this.updateActiveAuthenticationStep();

      // Return the parsed FlowResponse
      return flowResponse;
    }

    // Return null if the response is falsy
    return null;
  }

  sendOtpToMobile(): Observable<boolean> {
    return this.httpService.post("auth/flows/steps/otp").pipe(
      map((response) => {
        if (response?.data) {
          this.setOtpToken(response.data.otp_token);
          return true;
        }
        return false;
      }),
    );
  }

  createNewFlowToken(): Observable<string | null> {
    return this.httpService.post("auth/flows/tokens").pipe(
      map((response) => {
        if (response?.data) {
          this.accountService.setTokens(response.data);
          const expires = response.data.expires;
          this.authCoordinatorService.scheduleTokenRefresh(expires);
        }
        return null;
      }),
    );
  }

  getFlowAccessToken() {
    return this.flowAccessToken;
  }

  setFlowAccessToken(token: string) {
    this.flowAccessToken = token;
  }

  setFlow(value: Flow | null) {
    this.flow = value;
  }

  getFlow() {
    return this.flow;
  }

  setActiveFlowStep(value: FlowStep | null) {
    this.activeFlowStep = value;
  }

  getActiveFlowStep(): FlowStep | null {
    return this.activeFlowStep;
  }

  setOtpToken(value: string) {
    this.otpToken = value;
  }

  getOtpToken() {
    return this.otpToken;
  }

  getResendOtpTimer() {
    return this.resendOtpTimer;
  }

  setResendOtpTimer(value: number) {
    this.resendOtpTimer = value;
  }

  setAuthenticationStep(step: AuthenticationStep) {
    this.authenticationStep = step;
  }

  getAuthenticationStep() {
    return this.authenticationStep;
  }

  updateActiveAuthenticationStep() {
    switch (this.getActiveFlowStep()?.typeCode) {
      case FlowTypeCode.ADMIN_AUTH_METHOD_CHOICE:
        this.setAuthenticationStep(AuthenticationStep.SET_AUTHENTICATION_TYPE);
        break;
      case FlowTypeCode.ADMIN_GOOGLE_TWO_FACTOR_AUTHENTICATION:
        if (this.getActiveFlowStep()?.shouldGenerateQrCode) {
          this.setAuthenticationStep(
            AuthenticationStep.GOOGLE_AUTHENTICATOR_CODE,
          );
        } else {
          this.setAuthenticationStep(
            AuthenticationStep.VALIDATE_AUTHENTICATOR_OTP,
          );
        }
        break;
      case FlowTypeCode.OTP:
        this.setAuthenticationStep(AuthenticationStep.VALIDATE_MOBILE_OTP);
        break;
    }
  }

  setLoginAsSelectedStep() {
    this.setAuthenticationStep(AuthenticationStep.LOGIN);
  }

  isStepValid(step: AuthenticationStep) {
    return this.getAuthenticationStep() === step;
  }
}
